import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { State, US_STATES } from '../../../../shared/general/models/us-states.model';
import { CommonModule } from '@angular/common';
import { MatFormField, MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatCardModule } from '@angular/material/card';
import { MatDialogModule } from '@angular/material/dialog';
import { PropertySearchRequest } from '../../models/property.model';
import { MatSliderModule } from '@angular/material/slider';
import { PropertyService } from '../../services/property.service';
import { catchError, map, of, shareReplay } from 'rxjs';

@Component({
  selector: 'app-property-filter',
  imports: [
    CommonModule,
    MatFormField,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatCardModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatSliderModule
  ],
  templateUrl: './property-filter.html',
  styleUrl: './property-filter.scss',
})
export class PropertyFilter {
  private fb = inject(FormBuilder);

  @Output() filterChanged = new EventEmitter<Partial<PropertySearchRequest>>();
  @Output() onCancel = new EventEmitter<void>();
  @Input() set initialFilters(filters: PropertySearchRequest) {
    if (filters && this.searchForm) {
      this.searchForm.reset({}, { emitEvent: false });
      this.searchForm.patchValue(filters, { emitEvent: false });
    }
  }
  private propertyService = inject(PropertyService);
  searchForm: FormGroup;
  states: State[] = US_STATES;
  propertyTypes = ['house', 'townhouse', 'apartment', 'condo', 'villa', 'manufactured'];

  activeStates$ = this.propertyService.getActivePropertyStates().pipe(
      map((codes: string[]) =>
        this.states.filter(s => codes.includes(s.code))
      ),
      catchError(err => {
        console.error('Failed to load active states:', err);
        return of([]);
      }),
      shareReplay(1)
    );
  
  constructor() {
    this.searchForm = this.fb.group({
      city: [''],
      state: [null],
      zipCode: [''],
      propertyType: [null],
      minBedrooms: [0],
      maxBedrooms: [10],
      minBathrooms: [1],
      maxBathrooms: [10],
      minSleeps: [1],
      maxSleeps: [18]
    });
  }

  submit() {
    // Filter out null/empty values before emitting
    const rawValues = this.searchForm.value;
  
    // Create a clean object where 'null' or 'Any' selections are explicitly undefined
    // This ensures they don't get sent to the backend as active filters
    const cleanedFilters = Object.keys(rawValues).reduce((acc, key) => {
      const value = rawValues[key];
      // If value is null, undefined, or empty string, we treat it as "Any"
      acc[key] = (value === null || value === '' || value === undefined) ? undefined : value;
      return acc;
    }, {} as any);

    this.filterChanged.emit(cleanedFilters);
  }

  cancel() {
    this.onCancel.emit();
  }

  formatLabel(value: number): string {
    return `${value} mi`;
  }

  reset() {
    this.searchForm.reset();
  }
}
